import React, { useEffect, useRef, useState } from 'react';
import { Button, Collapse, Descriptions, Image, Input, Modal, Space, Spin, Tag, Tooltip, Typography, Upload, message } from 'antd';
import { CloseCircleFilled, CodepenOutlined, PaperClipOutlined, PictureOutlined, SendOutlined } from '@ant-design/icons';
import { ingestionApi } from '../ingestionApi';
import { GalleryPickerModal } from './GalleryPickerModal';

const { Text, Paragraph } = Typography;

const score = (n) => (n >= 4 ? 'success' : n >= 3 ? 'warning' : 'error');
const usd = (n) => (n == null ? 'unpriced' : `$${n.toFixed(2)}`);
const VIEW_LABELS = { front: 'Front', three_quarter: 'Three-quarter', side: 'Side', back_quarter: 'Back', containers: 'Containers' };
const MAX_IMAGES = 6;

const when = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const sameDay = d.toDateString() === new Date().toDateString();
    return sameDay
        ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** A stored render, fetched with the admin token (an <img src> would carry none). */
function BuilderRender({ itemId, variantId, view, runId }) {
    const [src, setSrc] = useState(null);
    useEffect(() => {
        let url = null;
        let cancelled = false;
        ingestionApi
            .getBuilderRenderBlob(itemId, variantId, view, runId)
            .then((blob) => {
                if (cancelled) return;
                url = URL.createObjectURL(blob);
                setSrc(url);
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
            if (url) URL.revokeObjectURL(url);
        };
    }, [itemId, variantId, view, runId]);
    return src ? <Image width={120} src={src} alt={VIEW_LABELS[view] || view} title={VIEW_LABELS[view] || view} /> : null;
}

const bubbleStyle = (mine) => ({
    maxWidth: '85%',
    padding: '8px 12px',
    borderRadius: 12,
    background: mine ? 'rgba(22, 119, 255, 0.18)' : 'rgba(128, 128, 128, 0.12)',
    alignSelf: mine ? 'flex-end' : 'flex-start',
});

/** What the admin asked for before a run: the text and the reference images. */
function AdminMessage({ feedback, at }) {
    return (
        <div style={bubbleStyle(true)}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {feedback.text && <Text>{feedback.text}</Text>}
                {feedback.imageUrls?.length > 0 && (
                    <Image.PreviewGroup>
                        <Space wrap size={4}>
                            {feedback.imageUrls.map((url) => (
                                <Image key={url} width={64} height={64} src={url} alt="reference" style={{ objectFit: 'cover', borderRadius: 6 }} />
                            ))}
                        </Space>
                    </Image.PreviewGroup>
                )}
                <Text type="secondary" style={{ fontSize: 11 }}>
                    {feedback.by?.name || feedback.by?.email || 'Admin'} · {when(at)}
                </Text>
            </Space>
        </div>
    );
}

/** One run of the builder: its verdict, renders, summary and the details behind them. */
function BuilderMessage({ itemId, variantId, run, sourceLabel }) {
    const judge = run.judge;
    const qa = run.qa;
    const qaLines = qa && (qa.failures.length > 0 || qa.warnings.length > 0);
    const details = (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {qaLines && (
                <div>
                    {qa.failures.map((f) => (
                        <div key={f}>
                            <Tag color="error">QA</Tag> {f}
                        </div>
                    ))}
                    {qa.warnings.map((w) => (
                        <div key={w}>
                            <Tag color="warning">QA</Tag> {w}
                        </div>
                    ))}
                </div>
            )}
            {judge?.notes && <Paragraph style={{ marginBottom: 0 }}>{judge.notes}</Paragraph>}
            {run.report && (
                <Descriptions size="small" column={1}>
                    <Descriptions.Item label="Containers">{run.report.containers}</Descriptions.Item>
                    <Descriptions.Item label="Materials">{run.report.materials}</Descriptions.Item>
                    {run.report.knownIssues?.length > 0 && (
                        <Descriptions.Item label="Known issues">{run.report.knownIssues.join(' · ')}</Descriptions.Item>
                    )}
                    <Descriptions.Item label="Confidence">{Math.round(run.report.confidence * 100)}%</Descriptions.Item>
                </Descriptions>
            )}
            <Text type="secondary" style={{ fontSize: 12 }}>
                {run.rounds} round{run.rounds === 1 ? '' : 's'}
                {run.stats ? ` · ${run.stats.tris} tris` : ''} · {usd(run.usage?.costUsd)}
                {run.usage?.judgeCostUsd != null ? ` + ${usd(run.usage.judgeCostUsd)} judge` : ''} · {Math.round((run.wallMs || 0) / 1000)} s
            </Text>
        </Space>
    );
    return (
        <div style={bubbleStyle(false)}>
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
                <Space wrap size={4}>
                    {run.ok ? <Tag color="success">{run.retexturedFrom ? 'retextured' : 'built'}</Tag> : <Tag color="error">{run.failure ? 'failed' : 'no model'}</Tag>}
                    {run.retexturedFrom && (
                        <Tooltip title="The approved model's script was run again with this variant's photos, changing only the materials">
                            <Tag color="geekblue">from {sourceLabel?.(run.retexturedFrom) || 'the group model'}</Tag>
                        </Tooltip>
                    )}
                    {qa && <Tag color={qa.pass ? 'success' : 'error'}>QA {qa.pass ? 'pass' : 'fail'}</Tag>}
                    {judge && (
                        <Tag color={score(judge.overall)}>
                            judge {judge.overall.toFixed(1)} / 5{judge.publishable ? ' · publishable' : ''}
                        </Tag>
                    )}
                </Space>
                {run.renders?.length > 0 && (
                    <Image.PreviewGroup>
                        <Space wrap size={4}>
                            {run.renders.map((view) => (
                                <BuilderRender key={view} itemId={itemId} variantId={variantId} view={view} runId={run.id} />
                            ))}
                        </Space>
                    </Image.PreviewGroup>
                )}
                {run.failure ? <Text type="danger">{run.failure}</Text> : run.report?.summary ? <Text>{run.report.summary}</Text> : null}
                {(qaLines || judge?.notes || run.report) && (
                    <Collapse size="small" ghost items={[{ key: 'details', label: 'Details', children: details }]} />
                )}
                <Text type="secondary" style={{ fontSize: 11 }}>
                    Opus builder · {when(run.finishedAt)}
                </Text>
            </Space>
        </div>
    );
}

/**
 * The composer: a message to the builder with optional reference images, uploaded
 * or picked from the product gallery, and a way to upload a model in place of the built one. Enter sends (Shift+Enter for a new line). An
 * empty message is a plain rebuild from the photos; text or images make the builder
 * start from its previous script and change what was asked.
 */
function Composer({ itemId, variantId, onSend, onUploadModel, hasModel, busy, hasRun }) {
    const modelInputRef = useRef(null);
    const [text, setText] = useState('');
    const [files, setFiles] = useState([]);
    const [galleryUrls, setGalleryUrls] = useState([]);
    const [galleryOpen, setGalleryOpen] = useState(false);
    const uploading = files.some((f) => f.status === 'uploading');
    const imageUrls = [...files.map((f) => f.response?.url).filter(Boolean), ...galleryUrls];
    const imageCount = files.length + galleryUrls.length;
    const full = imageCount >= MAX_IMAGES;
    const empty = !text.trim() && imageUrls.length === 0;

    const upload = async ({ file, onSuccess, onError }) => {
        try {
            onSuccess(await ingestionApi.uploadBuilderFeedbackImage(itemId, variantId, file));
        } catch (e) {
            message.error(e.message || 'Failed to upload the image');
            onError(e);
        }
    };

    const send = async () => {
        if (busy || uploading) return;
        await onSend(empty ? null : { text: text.trim() || null, imageUrls });
        setText('');
        setFiles([]);
        setGalleryUrls([]);
    };

    const label = empty ? (hasRun ? 'Rebuild' : 'Build') : 'Send';
    return (
        <div style={{ border: '1px solid rgba(128,128,128,0.35)', borderRadius: 12, padding: 8 }}>
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
                {imageCount > 0 && (
                    <Space wrap size={8} align="start">
                        {galleryUrls.map((url) => (
                            <div key={url} style={{ position: 'relative', width: 102, height: 102 }}>
                                <img src={url} alt="reference" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, display: 'block' }} />
                                {!busy && (
                                    <CloseCircleFilled
                                        role="button"
                                        aria-label="Remove image"
                                        onClick={() => setGalleryUrls((prev) => prev.filter((u) => u !== url))}
                                        style={{ position: 'absolute', top: -6, right: -6, fontSize: 18, color: 'rgba(0,0,0,0.7)', background: '#fff', borderRadius: 9, cursor: 'pointer' }}
                                    />
                                )}
                            </div>
                        ))}
                        {files.length > 0 && (
                            <Upload listType="picture-card" fileList={files} customRequest={upload} onChange={({ fileList }) => setFiles(fileList)} disabled={busy} showUploadList={{ showPreviewIcon: false }} />
                        )}
                    </Space>
                )}
                <Input.TextArea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    autoSize={{ minRows: 1, maxRows: 6 }}
                    disabled={busy}
                    variant="borderless"
                    placeholder={hasRun ? 'Tell the builder what to change… (empty = rebuild from scratch)' : 'Anything the builder should know? (optional)'}
                    onPressEnter={(e) => {
                        if (e.shiftKey) return;
                        e.preventDefault();
                        send();
                    }}
                />
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space size={0}>
                        <Upload
                            accept="image/*"
                            multiple
                            fileList={files}
                            customRequest={upload}
                            onChange={({ fileList }) => setFiles(fileList.slice(0, Math.max(0, MAX_IMAGES - galleryUrls.length)))}
                            showUploadList={false}
                            disabled={busy || full}
                        >
                            <Tooltip title="Attach a reference image (a texture to use, a photo of the detail)">
                                <Button type="text" icon={<PaperClipOutlined />} disabled={busy || full} />
                            </Tooltip>
                        </Upload>
                        <Tooltip title="Attach product images from the gallery">
                            <Button type="text" icon={<PictureOutlined />} disabled={busy || full} onClick={() => setGalleryOpen(true)} />
                        </Tooltip>
                        {onUploadModel && (
                            <>
                                <input
                                    ref={modelInputRef}
                                    type="file"
                                    accept=".glb,.babylon"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        e.target.value = '';
                                        if (!file) return;
                                        if (!hasModel) {
                                            onUploadModel(file);
                                            return;
                                        }
                                        Modal.confirm({
                                            title: 'Replace the model?',
                                            content: `This will replace the existing model with ${file.name}. The builder's runs stay in the chat, and the earlier files stay listed as older versions.`,
                                            okText: 'Replace',
                                            onOk: () => onUploadModel(file),
                                        });
                                    }}
                                />
                                <Tooltip title="Upload a 3D model (.glb / .babylon) in place of the built one">
                                    <Button type="text" icon={<CodepenOutlined />} disabled={busy} onClick={() => modelInputRef.current?.click()} />
                                </Tooltip>
                            </>
                        )}
                        {imageCount > 0 && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {imageCount} image{imageCount === 1 ? '' : 's'}
                            </Text>
                        )}
                    </Space>
                    <Tooltip title={empty ? (hasRun ? 'Drops the model and builds it again from the photos' : 'Builds the model from the photos') : 'Rebuilds from the previous script with your feedback'}>
                        <Button type="primary" icon={<SendOutlined />} onClick={send} loading={busy} disabled={busy || uploading}>
                            {label}
                        </Button>
                    </Tooltip>
                </Space>
            </Space>
            <GalleryPickerModal
                itemId={itemId}
                variantId={variantId}
                open={galleryOpen}
                taken={imageUrls}
                max={MAX_IMAGES}
                onPick={(urls) => setGalleryUrls((prev) => [...prev, ...urls.filter((u) => !prev.includes(u))].slice(0, MAX_IMAGES - files.length))}
                onClose={() => setGalleryOpen(false)}
            />
        </div>
    );
}

/**
 * The conversation with the Opus builder about one variant: each run as the builder's
 * message (renders, verdict, summary) under the feedback it was built from, then the
 * composer for the next build. The host shows `BuilderStatus` in its own header.
 */
export function BuilderRunPanel({ itemId, variantId, run, runs, onRebuild, onUploadModel, busy, hasModel, sourceLabel }) {
    const thread = runs?.length ? runs : run ? [run] : [];
    const endRef = useRef(null);
    useEffect(() => {
        endRef.current?.scrollIntoView({ block: 'nearest' });
    }, [thread.length, busy]);

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 520, overflowY: 'auto', padding: '2px 2px' }}>
                {thread.length === 0 && (
                    <div style={bubbleStyle(false)}>
                        <Text>
                            {hasModel
                                ? 'This variant has a model that Opus did not build. Send a message (or just Build) to replace it with an Opus model made from the photos.'
                                : 'No model yet. Build it from the photos, or tell the builder anything it should know first.'}
                        </Text>
                    </div>
                )}
                {thread.map((r, i) => (
                    <React.Fragment key={r.id || r.startedAt || i}>
                        {r.feedback && (r.feedback.text || r.feedback.imageUrls?.length > 0) && <AdminMessage feedback={r.feedback} at={r.startedAt} />}
                        <BuilderMessage itemId={itemId} variantId={variantId} run={r} sourceLabel={sourceLabel} />
                    </React.Fragment>
                ))}
                {busy && (
                    <div style={bubbleStyle(false)}>
                        <Space>
                            <Spin size="small" />
                            <Text type="secondary">Building… a few rounds of build / render / compare, this takes a few minutes.</Text>
                        </Space>
                    </div>
                )}
                <div ref={endRef} />
            </div>
            {onRebuild && (
                <Composer itemId={itemId} variantId={variantId} onSend={onRebuild} onUploadModel={onUploadModel} hasModel={hasModel} busy={busy} hasRun={thread.length > 0 || hasModel} />
            )}
        </Space>
    );
}
